/**
 * JSON-LD (@graph) builders for homepage, static pages, and services.
 */

import {
  normalizeSeoDescription,
  resolveAbsoluteUrl,
  stripHtmlToPlainText,
} from './blogSchema';
import type { BlogSchemaFaqItem } from './blogSchema';

export const PROTECTED_PAGE_SLUGS = [
  'contact',
  'services',
  'team',
  'about',
  'blog',
  'gallery',
] as const;

export interface PageSchemaOrgInput {
  appName: string;
  baseUrl: string;
  logoUrl?: string;
  companyDescription?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
  socialUrls?: string[];
}

export interface BuildHomeGraphSchemaInput extends PageSchemaOrgInput {
  pageTitle?: string;
  pageDescription?: string;
  pageUrl?: string;
  searchActionUrl?: string;
  inLanguage?: string;
  faq?: BlogSchemaFaqItem[];
}

export interface BuildWebPageGraphSchemaInput extends PageSchemaOrgInput {
  title: string;
  description?: string;
  url: string;
  images?: string[];
  inLanguage?: string;
  datePublished?: string;
  dateModified?: string;
  authorName?: string;
  faq?: BlogSchemaFaqItem[];
}

export interface BuildServiceGraphSchemaInput extends PageSchemaOrgInput {
  name: string;
  description?: string;
  contentHtml?: string;
  url: string;
  images?: string[];
  serviceType?: string;
  category?: string;
  price?: number;
  showPrice?: boolean;
  currency?: string;
  inLanguage?: string;
}

export interface ServiceListingItem {
  id: number;
  name: string;
  description?: string;
  url: string;
  imageUrl?: string;
  category?: string;
  price?: number;
  showPrice?: boolean;
}

export interface BuildServicesListingGraphSchemaInput extends PageSchemaOrgInput {
  title: string;
  description?: string;
  url: string;
  images?: string[];
  services: ServiceListingItem[];
  inLanguage?: string;
  currency?: string;
}

export function resolveProtectedPageUrl(slug: string, baseUrl: string, isHubLink?: boolean): string {
  const path = isHubLink
    ? `/site/${slug}`
    : (PROTECTED_PAGE_SLUGS as readonly string[]).includes(slug)
      ? `/${slug}`
      : `/page/${slug}`;
  return resolveAbsoluteUrl(path, baseUrl);
}

/**
 * Origin used for @id fragments in JSON-LD.
 * Prefers canonical_url from app_settings, then VITE_APP_URL / runtime origin.
 */
export function resolveSchemaBaseUrl(options: {
  canonicalUrl?: string | null;
  envBaseUrl?: string | null;
  runtimeOrigin?: string | null;
}): string {
  if (options.canonicalUrl?.startsWith('http')) {
    try {
      return new URL(options.canonicalUrl).origin;
    } catch {
      // fall through
    }
  }

  const envBase = options.envBaseUrl?.trim();
  if (envBase?.startsWith('http')) {
    return envBase.replace(/\/$/, '');
  }

  const runtime = options.runtimeOrigin?.trim();
  if (runtime?.startsWith('http')) {
    return runtime.replace(/\/$/, '');
  }

  return 'https://example.com';
}

export function stripTitleBrandSuffix(title: string, appName?: string): string {
  if (!title) return title;
  if (appName) {
    const suffix = ` | ${appName}`;
    if (title.endsWith(suffix)) {
      return title.slice(0, -suffix.length).trim();
    }
  }
  return title.replace(/\s*\|\s*[^|]+$/, '').trim() || title;
}

function buildOrganizationNode(
  input: PageSchemaOrgInput,
  orgId: string
): Record<string, unknown> {
  const base = input.baseUrl.replace(/\/$/, '');
  const org: Record<string, unknown> = {
    '@type': 'Organization',
    '@id': orgId,
    name: input.appName,
    url: base,
  };

  if (input.companyDescription) {
    org.description = input.companyDescription;
  }

  if (input.logoUrl) {
    org.logo = {
      '@type': 'ImageObject',
      url: resolveAbsoluteUrl(input.logoUrl, input.baseUrl),
    };
  }

  if (input.contactEmail) {
    org.contactPoint = {
      '@type': 'ContactPoint',
      email: input.contactEmail,
      contactType: 'customer service',
      ...(input.contactPhone ? { telephone: input.contactPhone } : {}),
    };
  }

  if (input.address) {
    org.address = {
      '@type': 'PostalAddress',
      addressLocality: input.address,
    };
  }

  const sameAs = (input.socialUrls || []).filter(Boolean);
  if (sameAs.length > 0) {
    org.sameAs = sameAs;
  }

  return org;
}

function buildOfferNode(options: {
  price: number;
  currency: string;
  url: string;
}): Record<string, unknown> {
  return {
    '@type': 'Offer',
    price: options.price,
    priceCurrency: options.currency,
    url: options.url,
    availability: 'https://schema.org/InStock',
  };
}

function buildFaqGraphNode(
  faq: BlogSchemaFaqItem[],
  faqId: string,
  webpageId: string
): Record<string, unknown> {
  return {
    '@type': 'FAQPage',
    '@id': faqId,
    isPartOf: { '@id': webpageId },
    mainEntity: faq.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: stripHtmlToPlainText(item.answer),
      },
    })),
  };
}

function resolveSearchActionTarget(searchActionUrl: string, baseUrl: string): string {
  if (searchActionUrl.startsWith('http://') || searchActionUrl.startsWith('https://')) {
    return searchActionUrl;
  }
  return resolveAbsoluteUrl(searchActionUrl, baseUrl);
}

function toImageArray(images: string[] | undefined, baseUrl: string): string[] | undefined {
  const resolved = (images || [])
    .filter(Boolean)
    .map((img) => resolveAbsoluteUrl(img, baseUrl));
  return resolved.length > 0 ? resolved : undefined;
}

export function buildHomeGraphSchema(input: BuildHomeGraphSchemaInput) {
  const base = input.baseUrl.replace(/\/$/, '');
  const orgId = `${base}/#organization`;
  const websiteId = `${base}/#website`;
  const webpageId = `${base}/#webpage`;
  const pageUrl = input.pageUrl
    ? resolveAbsoluteUrl(input.pageUrl, input.baseUrl)
    : `${base}/`;

  const pageName = stripTitleBrandSuffix(
    input.pageTitle || input.appName,
    input.appName
  );
  const pageDescription = normalizeSeoDescription(
    input.pageDescription || input.companyDescription || ''
  );

  const graph: Record<string, unknown>[] = [
    buildOrganizationNode(input, orgId),
    {
      '@type': 'WebSite',
      '@id': websiteId,
      name: input.appName,
      url: base,
      publisher: { '@id': orgId },
      ...(input.searchActionUrl
        ? {
            potentialAction: {
              '@type': 'SearchAction',
              target: resolveSearchActionTarget(input.searchActionUrl, input.baseUrl),
              'query-input': 'required name=search_term_string',
            },
          }
        : {}),
      ...(input.inLanguage ? { inLanguage: input.inLanguage } : {}),
    },
    {
      '@type': 'WebPage',
      '@id': webpageId,
      url: pageUrl,
      name: pageName,
      ...(pageDescription ? { description: pageDescription } : {}),
      isPartOf: { '@id': websiteId },
      about: { '@id': orgId },
      ...(input.inLanguage ? { inLanguage: input.inLanguage } : {}),
    },
  ];

  if (input.faq && input.faq.length > 0) {
    graph.push(buildFaqGraphNode(input.faq, `${base}/#faq`, webpageId));
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}

export function buildWebPageGraphSchema(input: BuildWebPageGraphSchemaInput) {
  const base = input.baseUrl.replace(/\/$/, '');
  const orgId = `${base}/#organization`;
  const pageUrl = resolveAbsoluteUrl(input.url, input.baseUrl);
  const webpageId = `${pageUrl}#webpage`;

  const description = input.description
    ? normalizeSeoDescription(input.description)
    : undefined;
  const images = toImageArray(input.images, input.baseUrl);

  const webpage: Record<string, unknown> = {
    '@type': 'WebPage',
    '@id': webpageId,
    url: pageUrl,
    name: input.title,
    ...(description ? { description } : {}),
    isPartOf: { '@id': `${base}/#website` },
    about: { '@id': orgId },
    publisher: { '@id': orgId },
    ...(input.inLanguage ? { inLanguage: input.inLanguage } : {}),
    ...(images ? { image: images } : {}),
    ...(input.datePublished ? { datePublished: input.datePublished } : {}),
    ...(input.dateModified ? { dateModified: input.dateModified } : {}),
    ...(input.authorName
      ? { author: { '@type': 'Person', name: input.authorName } }
      : {}),
  };

  const graph: Record<string, unknown>[] = [
    buildOrganizationNode(input, orgId),
    webpage,
  ];

  if (input.faq && input.faq.length > 0) {
    graph.push(buildFaqGraphNode(input.faq, `${pageUrl}#faq`, webpageId));
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}

export function buildServiceGraphSchema(input: BuildServiceGraphSchemaInput) {
  const base = input.baseUrl.replace(/\/$/, '');
  const orgId = `${base}/#organization`;
  const pageUrl = resolveAbsoluteUrl(input.url, input.baseUrl);
  const webpageId = `${pageUrl}#webpage`;
  const serviceId = `${pageUrl}#service`;
  const currency = input.currency || 'RON';

  const descriptionSource =
    input.description ||
    stripHtmlToPlainText(input.contentHtml || '');
  const description = descriptionSource
    ? normalizeSeoDescription(descriptionSource)
    : undefined;
  const images = toImageArray(input.images, input.baseUrl);

  const service: Record<string, unknown> = {
    '@type': 'Service',
    '@id': serviceId,
    name: input.name,
    url: pageUrl,
    provider: { '@id': orgId },
    ...(description ? { description } : {}),
    ...(images ? { image: images } : {}),
    ...(input.serviceType || input.category
      ? { serviceType: input.serviceType || input.category }
      : {}),
    ...(input.inLanguage ? { inLanguage: input.inLanguage } : {}),
  };

  if (input.showPrice && input.price && input.price > 0) {
    service.offers = buildOfferNode({
      price: input.price,
      currency,
      url: pageUrl,
    });
  }

  const webpage: Record<string, unknown> = {
    '@type': 'WebPage',
    '@id': webpageId,
    url: pageUrl,
    name: input.name,
    ...(description ? { description } : {}),
    isPartOf: { '@id': `${base}/#website` },
    about: { '@id': orgId },
    publisher: { '@id': orgId },
    mainEntity: { '@id': serviceId },
    ...(input.inLanguage ? { inLanguage: input.inLanguage } : {}),
    ...(images ? { image: images } : {}),
  };

  return {
    '@context': 'https://schema.org',
    '@graph': [buildOrganizationNode(input, orgId), webpage, service],
  };
}

export function buildServicesListingGraphSchema(
  input: BuildServicesListingGraphSchemaInput
) {
  const base = input.baseUrl.replace(/\/$/, '');
  const orgId = `${base}/#organization`;
  const pageUrl = resolveAbsoluteUrl(input.url, input.baseUrl);
  const webpageId = `${pageUrl}#webpage`;
  const currency = input.currency || 'RON';

  const description = input.description
    ? normalizeSeoDescription(input.description)
    : undefined;
  const images = toImageArray(input.images, input.baseUrl);

  const serviceNodes: Record<string, unknown>[] = [];

  const itemListElement = input.services.map((svc, index) => {
    const svcUrl = resolveAbsoluteUrl(svc.url, input.baseUrl);
    const svcId = `${svcUrl}#service`;

    const serviceNode: Record<string, unknown> = {
      '@type': 'Service',
      '@id': svcId,
      name: svc.name,
      url: svcUrl,
      provider: { '@id': orgId },
      ...(svc.description
        ? { description: normalizeSeoDescription(stripHtmlToPlainText(svc.description)) }
        : {}),
      ...(svc.category ? { serviceType: svc.category } : {}),
      ...(svc.imageUrl
        ? { image: [resolveAbsoluteUrl(svc.imageUrl, input.baseUrl)] }
        : {}),
      ...(svc.showPrice && svc.price && svc.price > 0
        ? {
            offers: buildOfferNode({
              price: svc.price,
              currency,
              url: svcUrl,
            }),
          }
        : {}),
    };

    serviceNodes.push(serviceNode);

    const item: Record<string, unknown> = {
      '@type': 'ListItem',
      position: index + 1,
      item: { '@id': svcId },
    };
    return item;
  });

  const graph: Record<string, unknown>[] = [
    buildOrganizationNode(input, orgId),
    {
      '@type': 'WebPage',
      '@id': webpageId,
      url: pageUrl,
      name: input.title,
      ...(description ? { description } : {}),
      isPartOf: { '@id': `${base}/#website` },
      about: { '@id': orgId },
      publisher: { '@id': orgId },
      ...(input.inLanguage ? { inLanguage: input.inLanguage } : {}),
      ...(images ? { image: images } : {}),
      mainEntity: {
        '@type': 'ItemList',
        '@id': `${pageUrl}#itemlist`,
        itemListElement,
      },
    },
    ...serviceNodes,
  ];

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}
